import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { RxCross2 } from 'react-icons/rx';
import { Separator } from '@radix-ui/react-separator';
import { Document, Page, pdfjs } from 'react-pdf';
import DialogRejected from '../DialogRejected';
import DialogApproved from '../DialogApproved';
import DialogMoreInfo from '../DialogMoreInfo';
import { ManagementService } from '../../../../../services/ManagementService';
import { errorToast } from '../../../../../utils/toasts';
import { errorMessageAdapter } from '../../../../../adapters';
import { AxiosError, AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from '../../../../../hooks/useLocalStorage';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  useDialogApprovalDocumentStore,
  useDialogMoreInfoDocumentStore,
  useDialogReproveDocumentStore
} from '../../store/dialogs.store';
import { t } from 'i18next';
import { downloadFile } from '../../../../../utils/downloadFile';
import { ICompany } from '../../../../../services/Interfaces/IManagemenntService';
import { useApi } from '../../../../../services/hooks/apiHook';
import DialogConfirmedCompany from '../ListCompanyDocument/DialogConfirmed/DialogConfirmedCompany';
import { useAppSelector } from '../../../../../hooks/hooksRedux';
import { RootState } from '../../../../../store';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`,
  import.meta.url
).toString();

interface VisualizeDocumentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareholderSlug: string;
  documentSlug: string;
  documentUrl: string;
  onDocumentApproved: (documentSlug: string, typeDialog: string) => void;
  company: ICompany;
}

const VisualizeDocument = ({
  open,
  onOpenChange,
  shareholderSlug,
  documentSlug,
  documentUrl,
  onDocumentApproved,
  company
}: VisualizeDocumentProps) => {
  const [fileUrlAws, setFileUrlAws] = React.useState('');
  const [openApprovedTransferCompany, setOpenApprovedTransferCompany] = useState<{
    action: string;
    openConfirmed: boolean;
  }>({
    action: '',
    openConfirmed: false
  });

  const { setOpen: setOpenRejected }: any = useDialogReproveDocumentStore();
  const { setOpen: setOpenApproved }: any = useDialogApprovalDocumentStore();

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);

  const newCompanyType = 'new_company';

  const [token] = useLocalStorage('access-token', null);

  const headers = { 'Content-Language': legalPersonalitySlug, Authorization: `Bearer ${token}` };

  const documentWrapperRef = React.useRef<HTMLDivElement>(null);
  const api: AxiosInstance = useApi();

  const getDocument = async () => {
    try {
      const response: any = await ManagementService.getDocumentUrl(
        api,
        {
          arquivos: [
            {
              path: documentUrl
            }
          ]
        },
        {
          headers
        }
      );
      setFileUrlAws(decodeURI(response.links[0].link));
    } catch (e) {
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  const handleActionDialogTransferCompany = (actionValue: string) => {
    setOpenApprovedTransferCompany({ action: actionValue, openConfirmed: true });
  };

  const renderDialog = () => {
    if (company.type === newCompanyType) {
      return (
        <>
          <DialogRejected
            shareholderSlug={shareholderSlug}
            documentSlug={documentSlug}
            onDocumentApproved={onDocumentApproved}
            onClose={() => onOpenChange(false)}
          />

      <DialogApproved
        shareholderSlug={shareholderSlug}
        documentSlug={documentSlug}
        onDocumentApproved={onDocumentApproved}
        onClose={() => onOpenChange(false)}
      />
        </>
      );
    }

    return (
      <>
        <DialogRejected
          shareholderSlug={shareholderSlug}
          documentSlug={documentSlug}
          onDocumentApproved={onDocumentApproved}
          onClose={() => onOpenChange(false)}
        />

        <DialogConfirmedCompany
          documentId={documentSlug}
          headers={headers}
          onDocumentApproved={openApprovedTransferCompany}
        />
      </>
    );
  };

  useEffect(() => {
    if (documentUrl) {
      getDocument();
    }
  }, [documentUrl]);

  return (
    <>
      {company && renderDialog()}

      <DialogMoreInfo
        shareholderSlug={shareholderSlug}
        documentSlug={documentSlug}
        onDocumentApproved={onDocumentApproved}
      />

      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm data-[state=open]:animate-overlayShow" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[95vh] w-[95%] max-w-4xl translate-x-[-50%] translate-y-[-50%] rounded-xl bg-white shadow-2xl focus:outline-none data-[state=open]:animate-contentShow overflow-hidden">
            <Dialog.Title className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold text-gray-800">
                  {t('others.othVisualiseDocument')}
                </h1>
                <Dialog.Close className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Dialog.Close>
              </div>
            </Dialog.Title>
            <div className="p-6">
              <div className="h-[70vh] w-full bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center" ref={documentWrapperRef}>
                <div className="w-full h-full flex flex-col items-center justify-center overflow-auto">
                  <Document 
                    file={fileUrlAws} 
                    className="flex flex-col items-center"
                    loading={
                      <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    }
                    error={
                      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p>Erro ao carregar documento</p>
                      </div>
                    }
                  >
                    <Page 
                      pageIndex={0} 
                      renderAnnotationLayer={false} 
                      renderTextLayer={false}
                      scale={1.2}
                      className="shadow-lg border border-gray-300 bg-white"
                    />
                  </Document>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-center gap-4">
                <button
                  className="flex items-center justify-center px-6 py-3 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-red-700 font-medium transition-colors"
                  onClick={() => {
                    setOpenRejected(true);
                    handleActionDialogTransferCompany('reproved');
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('others.othRejected')}
                </button>

                <button
                  className="flex items-center justify-center px-6 py-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-medium transition-colors"
                  onClick={() => downloadFile(fileUrlAws, 'document.pdf')}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('labels.download') || 'Download'}
                </button>

                <button
                  className="flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium transition-colors shadow-lg"
                  onClick={() => {
                    setOpenApproved(true);
                    handleActionDialogTransferCompany('approved');
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('others.othApproved')}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

export default VisualizeDocument;
